import moment from 'moment';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { getProfileLocation } from '../../../../api/ProfileClientView/ProfileClientView';
import { PublicProvider } from '../../../../api/Providers';
import { SITE_ROOT } from '../../../../config';
import { PublicProviderProfile } from '../../../../modules/Api.types';
import { ProviderServiceGroup } from '../../../../api/Providers/ServiceGroups';
import { toSlugCase } from '../../../../modules/stringUtils';

type AggregateRating = {
  '@type': 'AggregateRating',
  ratingValue: string,
  ratingCount: string,
  reviewCount?: string,
};

const DESCRIPTION_PREFIX = 'Book beauty and barber appointments online'
  + ' with top professionals and discover new professionals and services in your area.';

function getCanonical(profile: PublicProvider | ClientProviderProfile) {
  return `${SITE_ROOT}/m/v/${profile.vanity_url}`;
}

export function buildPageData(profile: PublicProviderProfile | ClientProviderProfile) {
  const canonical = getCanonical(profile);
  const datePublished = moment(profile.creation_time).format('YYYY-MM-DD HH:mm:ss');

  return {
    '@context': 'https://schema.org',
    '@id': canonical,
    '@type': 'WebPage',
    isPartOf: {
      '@id': `${SITE_ROOT}#WebSite`,
    },
    name: profile.name,
    description: `${DESCRIPTION_PREFIX} | ${profile.name}`,
    datePublished,
    dateModified: new Date().toISOString(),
  };
}

export function buildBreadCrumbData(profile: PublicProviderProfile | ClientProviderProfile) {
  const itemListElement = [];
  const canonical = getCanonical(profile);
  const location = getProfileLocation(profile);

  if (location?.city && location?.state) {
    const searchUrl = `${SITE_ROOT}/m/search/${toSlugCase(location.city)}-${toSlugCase(location.state)}`;
    itemListElement.push({
      '@type': 'ListItem',
      position: itemListElement.length + 1,
      item: {
        name: `${location.city}, ${location.state}`,
        '@id': searchUrl,
      },
    });

    if (profile.profession) {
      itemListElement.push({
        '@type': 'ListItem',
        position: itemListElement.length + 1,
        item: {
          name: profile.profession,
          '@id': `${searchUrl}/${toSlugCase(profile.profession)}`,
        },
      });
    }
  }

  itemListElement.push({
    '@type': 'ListItem',
    position: itemListElement.length + 1,
    item: {
      name: profile.name,
      '@id': canonical,
    },
  });

  return {
    '@context': 'https://schema.org',
    '@id': '#BreadcrumbList',
    '@type': 'BreadcrumbList',
    '@reverse': {
      breadcrumb:
        {
          '@id': canonical,
        },
    },
    itemListElement,
  };
}

function getPriceRange(serviceGroups?: Array<ProviderServiceGroup>): [number, number] {
  let minPrice = 0;
  let maxPrice = 0;

  if (!serviceGroups) {
    return null;
  }
  minPrice = Number.MAX_VALUE;
  maxPrice = Number.MIN_VALUE;

  serviceGroups.forEach(serviceGroup => {
    serviceGroup.services.forEach(service => {
      const cost = parseFloat(service.cost);
      if (cost < minPrice) {
        minPrice = cost;
      }
      if (cost > maxPrice) {
        maxPrice = cost;
      }
    });
  });

  return [
    minPrice,
    maxPrice,
  ];
}

export function buildProviderEntityData(
  profile: PublicProviderProfile | ClientProviderProfile,
  serviceGroups: ProviderServiceGroup[] = [],
  ratingsCount: number = 0,
  reviewCount: number = 0,
) {
  const canonical = getCanonical(profile);
  const [priceBottom, priceTop] = getPriceRange(serviceGroups);
  const location = getProfileLocation(profile);
  const result = {
    '@context': 'https://schema.org',
    '@id': '#MainEntityOfPage',
    '@type': 'HealthAndBeautyBusiness',
    logo: 'https://d4u5qzkfpc9yt.cloudfront.net/ss_logo_01.png',
    description: `${DESCRIPTION_PREFIX} | ${profile.name}`,
    mainEntityOfPage: {
      '@id': canonical,
    },
    makesOffer: {
      '@type': 'Offer',
      businessFunction: 'http://purl.org/goodrelations/v1#Sell',
    },
    url: canonical,
    priceRange: `US$${priceBottom} - US$${priceTop}`,
  } as any;

  const streetAddress = location?.mobile_business ? 'Mobile Business' : [
    location?.address1,
    location?.address2,
  ].filter(line => !!line).join(' ');

  result.name = [
    profile.name,
    streetAddress,
    location?.city,
    location?.state,
    location?.zipcode,
  ].filter(part => !!part).join(' ');

  if (profile.profile_photo) {
    result.image = {
      '@type': 'ImageObject',
      width: '200',
      height: '200',
      url: `${profile.profile_photo}_200x200.jpg`,
    };
  }

  if (location?.address1 && location?.coords?.latitude && location?.coords?.longitude) {
    result.address = {
      '@type': 'PostalAddress',
      addressCountry: {
        name: 'US',
      },
      addressRegion: location.state,
      streetAddress: location.address1,
    };

    result.geo = {
      '@id': '#GeoCoordinates',
      '@type': 'GeoCoordinates',
      latitude: location.coords.latitude,
      longitude: location.coords.longitude,
    };

    result.makesOffer.areaServed = {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@id': '#GeoCoordinates',
      },
      geoRadius: {
        '@type': 'Distance',
        name: '20 miles',
      },
    };
  }

  if (ratingsCount && ratingsCount > 0 && profile.rating_stars && profile.rating_stars > 0) {
    const aggregateRating: AggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: `${profile.rating_stars}`,
      ratingCount: `${ratingsCount}`,
    };

    if (reviewCount && reviewCount > 0) {
      aggregateRating.reviewCount = `${reviewCount}`;
    }

    result.aggregateRating = aggregateRating;
  }

  return result;
}
